import { getAuthHeader } from "../../lib/authFetch";

// Strips HTML tags so Markup AI receives plain text to check against the brand style guide.
function stripHtml(html) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return div.textContent || div.innerText || "";
}

// Normalizes a raw Markup AI issue (per its documented Issue schema: severity,
// explanation, position: { start, end, text }, suggestion, guideline_name/category)
// into the shape Sidebar2's issue list already renders: { severity, reason,
// exact_text, suggested_replacement, location_hint }.
function normalizeIssue(raw) {
  const reason = raw.explanation || raw.rationale || "—";
  return {
    severity: (raw.severity || "low").toLowerCase(),
    // persona_name is stamped server-side when a check (e.g. persona) runs once per
    // configured persona and merges the results, so callers can see who flagged it.
    reason: raw.persona_name ? `[${raw.persona_name}] ${reason}` : reason,
    exact_text: raw.position?.text || "",
    suggested_replacement: raw.suggestion || raw.suggestions?.[0] || "",
    location_hint: raw.guideline_name || raw.category || raw.context_surface || "",
  };
}

// Must match MARKUP_AI_CHECK_KEYS in server/server.js.
const MARKUP_AI_CHECK_KEYS = [
  "brand_voice",
  "persona",
  "terminology",
  "generic_claims",
  "source_authority",
  "freshness",
  "focus_agent",
  "answer_authority",
  "sentence_clarity",
  "ai_voice_detector",
  "snippet_readiness",
  "key_takeaways",
];

// Runs every Markup AI check (brand voice, persona, terminology, etc.) against
// the current article and returns { [agentKey]: { issues: [...] } | { error } }.
// Each check is its own request (rather than one bulk request) so no single
// HTTP round trip risks exceeding Heroku's 30s router timeout.
export const checkMarkupAIAll = async (htmlContent) => {
  const MARKUP_AI_BASE =
    (process.env.NODE_ENV === "development"
      ? "http://localhost:5000"
      : window.location.origin) + "/markup-ai/v1";

  const text = stripHtml(htmlContent);
  const authHeader = await getAuthHeader();

  const entries = await Promise.all(
    MARKUP_AI_CHECK_KEYS.map(async (key) => {
      try {
        const response = await fetch(`${MARKUP_AI_BASE}/check`, {
          method: "POST",
          headers: { "content-type": "application/json", ...authHeader },
          body: JSON.stringify({ text, key }),
        });
        const data = await response.json();
        console.log(`Markup AI raw response [${key}]:`, data);
        if (!response.ok) {
          throw new Error(data?.error || `HTTP ${response.status}`);
        }
        return [key, { issues: (data?.issues || []).map(normalizeIssue) }];
      } catch (e) {
        return [key, { error: String(e?.message || e) }];
      }
    })
  );

  return Object.fromEntries(entries);
};
